import React, { useLayoutEffect, useMemo, useState } from 'react';
import ContentEditable from '../../../utils/SafeContentEditable';
import { countCodeLines } from '../utils/codeContent';
import { measureLogicalLineHeights } from '../utils/measureLineHeights';

export const CodeBlockBody = ({
  blockId,
  content,
  wrapText,
  showLineNumbers,
  growScrollRef,
  contentEditableRef,
  onChange,
  onKeyDown,
  onPaste,
}) => {
  const lineCount = countCodeLines(content);
  const [lineHeights, setLineHeights] = useState(null);

  // When wrap is on, size each gutter cell to the logical line's rendered
  // height so soft-wraps don't desync numbers from code (like Sublime).
  useLayoutEffect(() => {
    if (!showLineNumbers || !wrapText) {
      setLineHeights(null);
      return undefined;
    }
    const el = contentEditableRef.current;
    if (!el) return undefined;

    let raf = 0;
    const apply = () => {
      setLineHeights(measureLogicalLineHeights(el));
    };
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(apply);
    };

    apply();

    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [showLineNumbers, wrapText, content, lineCount, contentEditableRef]);

  const lineNumbers = useMemo(
    () => (
      <div className="code-block-lines" aria-hidden="true">
        {Array.from({ length: lineCount }, (_, i) => (
          <div
            key={i}
            className="code-block-line-number"
            style={
              lineHeights && lineHeights[i] != null
                ? { height: lineHeights[i] }
                : undefined
            }
          >
            {i + 1}
          </div>
        ))}
      </div>
    ),
    [lineCount, lineHeights]
  );

  return (
    <div className={`code-block-body ${wrapText ? 'wrap-mode' : ''}`}>
      {showLineNumbers && lineNumbers}
      <div ref={growScrollRef} className="code-block-grow">
        <ContentEditable
          innerRef={contentEditableRef}
          html={content}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          className="code-block-editable doc-editable"
          data-code-block={blockId}
          tagName="div"
          placeholder="Write your code…"
        />
      </div>
    </div>
  );
};
